import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Target, Search, Brain, Lightbulb, ChevronRight } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  'Food Safety': 'bg-red-50 text-red-700 border-red-200',
  'Guest Experience': 'bg-blue-50 text-blue-700 border-blue-200',
  'Speed of Service': 'bg-orange-50 text-orange-700 border-orange-200',
  'Operations': 'bg-slate-100 text-slate-700 border-slate-200',
  'Cleanliness': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  'Quality': 'bg-amber-50 text-amber-700 border-amber-200',
  'Profit': 'bg-green-50 text-green-700 border-green-200',
  'Team': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Business Brain': 'bg-purple-50 text-purple-700 border-purple-200',
};

export default function BestPractices() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ tips_count: 0, brain_count: 0 });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/best-practices`);
        setItems(res.data.items || []);
        setMeta({ tips_count: res.data.tips_count, brain_count: res.data.brain_count });
      } catch (e) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const categories = ['All', ...new Set(items.map(i => i.category))];
  const filtered = items.filter(i =>
    (filter === 'All' || i.category === filter) &&
    (!search ||
      i.title.toLowerCase().includes(search.toLowerCase()) ||
      i.tip.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
          <Target className="w-6 h-6" /> Best Practices
        </h1>
        <p className="text-white/80 text-sm mt-1">
          {meta.tips_count} operational tips + {meta.brain_count} insights from Business Brain
        </p>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search tips & insights..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 rounded-lg"
          data-testid="best-practices-search"
        />
      </div>

      {/* Category pills */}
      <div className="flex flex-wrap gap-1.5">
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setFilter(cat)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filter === cat
                ? 'bg-orange-500 text-white shadow-sm'
                : 'bg-white text-slate-600 border border-slate-200 hover:border-orange-300'
            }`}
            data-testid={`bp-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <p className="text-sm text-slate-400">Loading...</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-slate-400">No matches.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="best-practices-grid">
          {filtered.map(item => {
            const isBB = item.id.startsWith('bb-');
            const Icon = isBB ? Brain : Lightbulb;
            const colorClass = CATEGORY_COLORS[item.category] || 'bg-slate-50 text-slate-700 border-slate-200';
            return (
              <Card key={item.id} className="p-4 hover:shadow-md transition-shadow border-slate-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full border ${colorClass}`}>
                    {item.category}
                  </span>
                  <Icon className="w-3.5 h-3.5 text-slate-400 ml-auto" />
                </div>
                <h3 className="text-sm font-bold text-slate-900 mb-1.5 leading-snug">{item.title}</h3>
                <p className="text-xs text-slate-600 leading-relaxed">{item.tip}</p>
                {isBB && item.source && (
                  <p className="text-[10px] text-slate-400 mt-2 italic">From: {item.source}</p>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
